import React from 'react';
import { ArenaParticipant } from '@/hooks/useLiveArenas';
import { Button } from '@/components/ui/button';
import { Hand, Mic, X, Check } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';

interface HandRaisePanelProps {
  raisedHands: ArenaParticipant[];
  isHost: boolean;
  onPromote: (userId: string) => void;
  onDismiss: (userId: string) => void;
  className?: string;
}

export const HandRaisePanel: React.FC<HandRaisePanelProps> = ({
  raisedHands,
  isHost,
  onPromote,
  onDismiss,
  className,
}) => {
  if (raisedHands.length === 0) return null;

  return (
    <div
      className={cn(
        'bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl p-3',
        className
      )}
    >
      <div className="flex items-center gap-2 mb-2">
        <Hand className="w-4 h-4 text-amber-500" />
        <span className="text-sm font-medium">
          {raisedHands.length} hand{raisedHands.length > 1 ? 's' : ''} raised
        </span>
      </div>

      <div className="space-y-2 max-h-40 overflow-y-auto">
        {raisedHands.map((participant) => (
          <div
            key={participant.id}
            className="flex items-center gap-2 bg-background/50 rounded-lg p-2"
          >
            <img
              src={
                participant.profile?.photo_url ||
                'https://api.dicebear.com/7.x/avataaars/svg?seed=default'
              }
              alt=""
              className="w-8 h-8 rounded-full object-cover"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">
                {participant.profile?.name || 'Guest'}
              </p>
              <p className="text-xs text-muted-foreground">
                {participant.hand_raised_at &&
                  formatDistanceToNow(new Date(participant.hand_raised_at), {
                    addSuffix: true,
                  })}
              </p>
            </div>

            {isHost && (
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onPromote(participant.user_id)}
                  className="h-7 w-7 rounded-full bg-green-500/10 text-green-500 hover:bg-green-500/20"
                >
                  <Check className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onDismiss(participant.user_id)}
                  className="h-7 w-7 rounded-full bg-destructive/10 text-destructive hover:bg-destructive/20"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

interface HandRaiseButtonProps {
  isHandRaised: boolean;
  onToggle: () => void;
  disabled?: boolean;
  className?: string;
}

export const HandRaiseButton: React.FC<HandRaiseButtonProps> = ({
  isHandRaised,
  onToggle,
  disabled = false,
  className,
}) => {
  return (
    <Button
      variant="outline"
      size="lg"
      onClick={onToggle}
      disabled={disabled}
      className={cn(
        'rounded-full w-14 h-14 transition-all',
        isHandRaised
          ? 'bg-amber-500/10 border-amber-500 text-amber-500 animate-pulse'
          : 'bg-muted border-border text-muted-foreground',
        className
      )}
    >
      <Hand className={cn('w-6 h-6', isHandRaised && 'animate-bounce')} />
    </Button>
  );
};
